import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { MonitorSmartphone, Gamepad2, Armchair, ScanLine, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError, formatINR, formatHour } from "@/lib/format";
import QRScanner from "@/components/QRScanner";
import CountryBubbleMap from "@/components/CountryBubbleMap";

const SEAT_STATUS_COLOR = {
  free: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  occupied: "bg-red-500/20 text-red-300 border-red-500/40",
  reserved: "bg-[#D4B878]/20 text-[#E8D6A3] border-[#D4B878]/40",
};

export default function AdminConsole() {
  const [hubs, setHubs] = useState([]);
  const [hubId, setHubId] = useState(null);
  const [floor, setFloor] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [devices, setDevices] = useState([]);
  const [countryStats, setCountryStats] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/hubs");
      setHubs(data);
      if (data.length) setHubId(data[0].id);
    })();
  }, []);

  const loadAll = async () => {
    if (!hubId) return;
    const [f, b, d, cs] = await Promise.all([
      api.get(`/admin/floor/${hubId}`),
      api.get(`/admin/bookings?hub_id=${hubId}`),
      api.get(`/admin/devices?hub_id=${hubId}`),
      api.get(`/hubs/${hubId}/analytics/countries?days=30`).catch(() => ({ data: null })),
    ]);
    setFloor(f.data);
    setBookings(b.data);
    setDevices(d.data);
    setCountryStats(cs.data);
  };

  useEffect(() => {
    loadAll();
  }, [hubId]);

  const setDeviceStatus = async (id, status) => {
    try {
      await api.patch(`/admin/devices/${id}/status`, { status });
      toast.success("Device updated");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resourceStatus = (r) => {
    if (!r.bookings_today || r.bookings_today.length === 0) return "free";
    const now = new Date();
    const curHour = now.getHours();
    const inSession = r.bookings_today.find(
      (b) =>
        b.status === "checked_in" ||
        (b.status === "booked" && b.start_hour <= curHour && curHour < b.start_hour + b.duration_hours)
    );
    if (inSession) return "occupied";
    return "reserved";
  };

  return (
    <div className="space-y-8" data-testid="admin-console">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
            Hub operations
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
            Command center.
          </h1>
        </div>
        {hubs.length > 0 && (
          <div className="min-w-[260px]">
            <Select value={hubId || ""} onValueChange={setHubId}>
              <SelectTrigger data-testid="admin-hub-select" className="bg-white/5 border-white/10 rounded-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {hubs.map((h) => (
                  <SelectItem key={h.id} value={h.id}>
                    {h.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Tabs defaultValue="floor" className="w-full">
        <TabsList data-testid="admin-tabs" className="bg-white/5 border border-white/10 rounded-full p-1">
          <TabsTrigger value="floor" data-testid="admin-tab-floor" className="rounded-full">Live floor</TabsTrigger>
          <TabsTrigger value="scan" data-testid="admin-tab-scan" className="rounded-full">
            <ScanLine size={14} className="mr-1" /> QR scan
          </TabsTrigger>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings" className="rounded-full">Bookings</TabsTrigger>
          <TabsTrigger value="devices" data-testid="admin-tab-devices" className="rounded-full">Devices</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="rounded-full">
            <Globe2 size={14} className="mr-1" /> Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="floor" className="mt-8">
          {floor && (
            <div className="space-y-8">
              <div className="flex items-center gap-3 text-xs text-zinc-400">
                <LegendDot className="bg-emerald-500" /> Free
                <LegendDot className="bg-red-500" /> Occupied
                <LegendDot className="bg-[#D4B878]" /> Reserved
              </div>

              <Section title="Seats" icon={<Armchair size={18} />}>
                <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                  {floor.seats.map((s) => {
                    const st = resourceStatus(s);
                    return (
                      <div
                        key={s.id}
                        data-testid={`floor-seat-${s.code}`}
                        title={`${s.seat_type} ${s.code}`}
                        className={`aspect-square rounded-lg border text-[10px] flex items-center justify-center font-bold ${SEAT_STATUS_COLOR[st]}`}
                      >
                        {s.code}
                      </div>
                    );
                  })}
                </div>
              </Section>

              <Section title="Devices" icon={<MonitorSmartphone size={18} />}>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                  {floor.devices.map((d) => {
                    const st = resourceStatus(d);
                    return (
                      <div
                        key={d.id}
                        data-testid={`floor-device-${d.asset_id}`}
                        className={`rounded-xl border px-3 py-4 text-center ${SEAT_STATUS_COLOR[st]}`}
                      >
                        <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">
                          {d.type.toUpperCase()}
                        </div>
                        <div className="font-black text-sm mt-1">{d.asset_id}</div>
                      </div>
                    );
                  })}
                </div>
              </Section>

              <div className="grid md:grid-cols-4 gap-4">
                <Stat label="Seats" value={floor.seats.length} />
                <Stat label="Devices" value={floor.devices.length} />
                <Stat label="Bookings today" value={floor.bookings_today.length} />
                <Stat
                  label="Revenue today"
                  value={formatINR(
                    floor.bookings_today.reduce((a, b) => a + (b.price || 0), 0)
                  )}
                />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="scan" className="mt-8">
          <QRScanner hubId={hubId} />
        </TabsContent>

        <TabsContent value="bookings" className="mt-8">
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-zinc-500 uppercase text-[10px] tracking-[0.18em]">
                <tr>
                  <th className="text-left p-4">User</th>
                  <th className="text-left p-4">Resource</th>
                  <th className="text-left p-4">Date</th>
                  <th className="text-left p-4">Time</th>
                  <th className="text-left p-4">Price</th>
                  <th className="text-left p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-white/5" data-testid={`admin-booking-${b.id}`}>
                    <td className="p-4 font-semibold">{b.user_name}</td>
                    <td className="p-4 text-zinc-300">{b.resource_label}</td>
                    <td className="p-4 text-zinc-400">{b.date}</td>
                    <td className="p-4 text-zinc-400">
                      {formatHour(b.start_hour)}–{formatHour(b.start_hour + b.duration_hours)}
                    </td>
                    <td className="p-4 text-[#E8D6A3] font-bold">{formatINR(b.price)}</td>
                    <td className="p-4">
                      <Badge variant="outline" className="border-white/10 capitalize">
                        {b.status.replace("_", " ")}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-zinc-500">
                      No bookings yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="devices" className="mt-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {devices.map((d) => (
              <div
                key={d.id}
                data-testid={`device-card-${d.asset_id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {d.type === "ps5" ? <Gamepad2 size={16} /> : <MonitorSmartphone size={16} />}
                    <span className="font-bold">{d.asset_id}</span>
                  </div>
                  <Badge variant="outline" className="border-white/10 capitalize">
                    {d.status}
                  </Badge>
                </div>
                <div className="text-sm text-zinc-400">{d.model}</div>
                <div className="text-xs text-zinc-600">SN {d.serial}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["available", "maintenance", "retired"].map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant="outline"
                      data-testid={`device-${d.asset_id}-set-${s}`}
                      onClick={() => setDeviceStatus(d.id, s)}
                      className={`bg-white/5 border-white/10 text-xs rounded-full ${
                        d.status === s ? "ring-1 ring-[#D4B878]/50" : ""
                      }`}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Country analytics — where walk-ins to this hub actually come from. */}
        <TabsContent value="analytics" className="mt-8">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-1 flex items-center gap-1.5">
                  <Globe2 size={12} /> Walk-in country split · last 30 days
                </div>
                <div className="text-lg font-semibold">
                  Where this hub's customers come from
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  Bubble size ∝ booking count · cream = seat bookings, cyan = device (Mac) bookings
                </div>
              </div>
              {countryStats && (
                <div className="flex gap-3 text-[10px] uppercase tracking-[0.18em] text-white/40">
                  <div>
                    <div className="font-mono text-white text-lg" data-testid="hub-analytics-total">
                      {countryStats.total}
                    </div>
                    Total
                  </div>
                  <div>
                    <div className="font-mono text-white text-lg" data-testid="hub-analytics-countries-count">
                      {countryStats.per_country?.length || 0}
                    </div>
                    Countries
                  </div>
                  <div>
                    <div className="font-mono text-white text-lg" data-testid="hub-analytics-unknown">
                      {countryStats.unknown}
                    </div>
                    Unknown
                  </div>
                </div>
              )}
            </div>
            {countryStats && countryStats.per_country?.length > 0 ? (
              <div data-testid="hub-analytics-country-map">
                <CountryBubbleMap data={countryStats.per_country} />
              </div>
            ) : (
              <div className="text-xs text-white/40 italic p-6 text-center border border-dashed border-white/10 rounded">
                Not enough bookings in the last 30 days to draw a country map. Come back once you've hosted a handful of walk-ins.
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-500 mb-4">
        {icon} {title}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="text-2xl font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
        {label}
      </div>
    </div>
  );
}

function LegendDot({ className }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${className}`} />;
}
